import { CheckIcon, MinusIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { Fragment } from 'react';

import clsxm from '@/lib/clsxm';

interface Feature {
  name: string;
  highlight?: boolean;
  tiers?: {
    [K in 'Essential' | 'Premium' | 'Basic']?: boolean | string;
  };
}

interface Tier {
  name: 'Essential' | 'Premium';
  id: string;
  priceMonthly: string;
  mostPopular: boolean;
}

const tiers: Tier[] = [
  {
    name: 'Essential',
    id: 'tier-essential',
    priceMonthly: 'Easy-Peasy.AI',
    mostPopular: true,
  },
  {
    name: 'Premium',
    id: 'tier-premium',
    priceMonthly: 'ChatGPT',
    mostPopular: false,
  },
];

interface Section {
  name?: string;
  features: Feature[];
}

const sections: Section[] = [
  {
    features: [
      {
        name: 'AI Models',
        highlight: true,
      },
      {
        name: 'OpenAI',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'Anthropic',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Google',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'DeepSeek',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'xAI',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Meta',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Agent Capabilities',
        highlight: true,
      },
      {
        name: 'Build & Deploy Websites',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Browse the Web',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'Code Execution & Visualization',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'Deep Research with Reasoning',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'Create Presentations',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'File Operations (Upload, Edit, Download)',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'Image Generator',
        highlight: true,
      },
      {
        name: 'FLUX Pro Ultra',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Recraft v3',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Ideogram v2',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Media & Documents',
        highlight: true,
      },
      {
        name: 'Analyze Videos & Audio',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Chat with PDFs & Scanned Docs',
        tiers: {
          Essential: true,
          Premium: '(Plugins)',
        },
      },
      {
        name: 'Chat with Docs/PPT/CSV',
        tiers: {
          Essential: true,
          Premium: false,
        },
      },
      {
        name: 'Chat with YouTube Videos',
        tiers: {
          Essential: true,
          Premium: false,
        },
      },
      {
        name: 'Human-Like Voiceovers',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Core Features',
        highlight: true,
      },
      {
        name: 'Real-Time Web Search',
        tiers: { Essential: true, Premium: '(Plugins)' },
      },
      {
        name: 'Prebuilt Templates (300+)',
        tiers: {
          Essential: true,
          Premium: false,
        },
      },
      {
        name: 'Write in Brand Voice',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Custom Chatbots',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Team Workspace & Sharing',
        tiers: { Essential: true, Premium: true },
      },
      {
        name: 'REST API Access',
        tiers: { Essential: true, Premium: false },
      },
      {
        name: 'Price Start',
        highlight: true,
      },
      {
        name: 'Monthly Price',
        tiers: { Essential: '$8/month', Premium: '$20/month' },
      },
    ],
  },
];

export default function ChatGPTComparisonNew() {
  const { t } = useTranslation('marky');

  return (
    <div className='bg-white py-24 dark:bg-slate-800 sm:py-32'>
      <div className='mx-auto max-w-5xl px-6 lg:px-8'>
        <div className='mx-auto max-w-4xl text-center'>
          <h2 className='text-sm font-bold uppercase tracking-widest text-primary-600 dark:text-primary-400'>
            {t('Side by side')}
          </h2>
          <p className='mt-4 text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-5xl'>
            {t('Easy-Peasy.AI vs ChatGPT')}
          </p>
        </div>
        <p className='mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600 dark:text-slate-400'>
          {t(
            'More AI models, agent capabilities, and creative tools — at a fraction of the price.',
          )}
        </p>

        {/* xs to lg */}
        <div className='mx-auto mt-12 max-w-md space-y-8 sm:mt-16 lg:hidden'>
          {tiers.map((tier) => (
            <section
              key={tier.id}
              className={clsxm(
                tier.mostPopular
                  ? 'rounded-xl bg-gray-400/5 ring-1 ring-inset ring-gray-200'
                  : '',
                'p-8',
              )}
            >
              <p className='mt-2 flex items-baseline gap-x-1 text-2xl font-bold text-gray-900 dark:text-slate-200'>
                {tier.priceMonthly == 'Easy-Peasy.AI' ? (
                  <Image
                    src='/images/marky.png'
                    width='250'
                    height='36'
                    alt='Marky'
                    className='ml-3'
                  />
                ) : (
                  'ChatGPT'
                )}
              </p>

              <ul
                role='list'
                className='mt-10 space-y-4 text-sm leading-6 text-gray-900'
              >
                {sections.map((section) => (
                  <li key={section.name || 'section'}>
                    <ul role='list' className='space-y-4'>
                      {section.features.map((feature) => {
                        if (feature.highlight) {
                          return (
                            <li
                              key={feature.name}
                              className='text-lg font-bold text-indigo-600 dark:text-indigo-400'
                            >
                              {t(feature.name)}
                            </li>
                          );
                        }

                        if (!feature.tiers) {
                          return null;
                        }

                        const tierValue = feature.tiers[tier.name];
                        if (!tierValue) {
                          return null;
                        }

                        return (
                          <li key={feature.name} className='flex gap-x-3'>
                            <CheckIcon
                              className='h-6 w-5 flex-none text-indigo-600'
                              aria-hidden='true'
                            />
                            <span className='dark:text-slate-300'>
                              {t(feature.name)}{' '}
                              {typeof tierValue === 'string' && (
                                <span className='text-sm leading-6 text-gray-500'>
                                  ({tierValue})
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        {/* lg+ */}
        <div className='isolate mt-20 hidden lg:block'>
          <div className='relative -mx-8'>
            {tiers.some((tier) => tier.mostPopular) ? (
              <div className='absolute inset-x-4 inset-y-0 -z-10 flex'>
                <div
                  className='flex w-1/3 px-4'
                  aria-hidden='true'
                  style={{
                    marginLeft: `${
                      (tiers.findIndex((tier) => tier.mostPopular) + 1) * 33
                    }%`,
                  }}
                >
                  <div className='w-full rounded-t-xl border-x border-t border-gray-900/10 bg-gray-400/5 dark:border-white/10 dark:bg-white/5' />
                </div>
              </div>
            ) : null}
            <table className='w-full table-fixed border-separate border-spacing-x-8 text-left'>
              <colgroup>
                <col className='w-1/4' />
                <col className='w-1/4' />
                <col className='w-1/4' />
              </colgroup>

              <tbody>
                <tr>
                  <th scope='row'></th>
                  {tiers.map((tier) => (
                    <td key={tier.id} className='px-6 pt-12 xl:px-8'>
                      <div className='flex  items-center justify-center text-gray-900 dark:text-slate-200'>
                        <span className='text-3xl  font-bold'>
                          {tier.priceMonthly == 'Easy-Peasy.AI' ? (
                            <Image
                              src='/images/marky.png'
                              width='250'
                              height='36'
                              alt='Marky'
                              className='ml-3'
                            />
                          ) : (
                            'ChatGPT'
                          )}
                        </span>
                      </div>
                    </td>
                  ))}
                </tr>
                {sections.map((section, sectionIdx) => (
                  <Fragment key={sectionIdx}>
                    {section.name && (
                      <tr>
                        <th
                          scope='colgroup'
                          colSpan={3}
                          className={clsxm(
                            sectionIdx === 0 ? 'pt-8' : 'pt-16',
                            'pb-4 text-sm font-semibold leading-6 text-gray-900 dark:text-slate-500',
                          )}
                        >
                          {t(section.name)}
                          <div className='absolute inset-x-8 mt-4 h-px bg-gray-900/10 dark:bg-white/10' />
                        </th>
                      </tr>
                    )}
                    {section.features.map((feature) => (
                      <tr key={feature.name}>
                        <th
                          scope='row'
                          className={clsxm(
                            'py-4 text-sm leading-6 dark:text-slate-300',
                            feature.highlight
                              ? 'text-lg font-bold text-indigo-600 dark:text-indigo-400'
                              : 'font-semibold text-gray-900',
                          )}
                        >
                          {t(feature.name)}
                          <div className='absolute inset-x-8 mt-4 h-px bg-gray-900/5 dark:bg-white/5' />
                        </th>
                        {feature.tiers
                          ? tiers.map((tier) => {
                              if (!feature.tiers) return null;
                              const tierValue = feature.tiers[tier.name];
                              return (
                                <td key={tier.id} className='px-6 py-4 xl:px-8'>
                                  {typeof tierValue === 'string' ? (
                                    <div className='text-center text-sm leading-6 text-gray-500'>
                                      {tierValue}
                                    </div>
                                  ) : tierValue ? (
                                    <>
                                      <CheckIcon
                                        className='mx-auto h-5 w-5 text-indigo-600'
                                        aria-hidden='true'
                                      />
                                      <span className='sr-only'>
                                        Included in {tier.name}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <MinusIcon
                                        className='mx-auto h-5 w-5 text-gray-400'
                                        aria-hidden='true'
                                      />
                                      <span className='sr-only'>
                                        Not included in {tier.name}
                                      </span>
                                    </>
                                  )}
                                </td>
                              );
                            })
                          : tiers.map((tier) => (
                              <td
                                key={tier.id}
                                className='px-6 py-4 xl:px-8'
                              ></td>
                            ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className='mt-24 flex justify-center'>
        <div className='rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300'>
          <Link
            href='/login?preset=chat'
            className='group relative flex w-full items-center justify-center rounded-full bg-primary-600 px-8 py-4 text-base font-bold text-white hover:bg-primary-500 hover:-translate-y-1 transition-all duration-300 md:px-12 md:text-lg'
          >
            {t('Try it yourself for free')}
          </Link>
        </div>
      </div>
    </div>
  );
}
